import { Link } from "react-router-dom";
import type { LiveItem } from "@/mocks/lives";
import { LiveStatusBadge, SignalMeter } from "@/components/base/LiveIndicators";
import { cn, formatViewers } from "@/utils/format";

interface LiveCardProps {
  item: LiveItem;
  className?: string;
  index?: number;
}

export default function LiveCard({ item, className, index }: LiveCardProps) {
  return (
    <article
      className={cn(
        "group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-100 transition-colors hover:border-primary-500/60",
        className
      )}
    >
      <Link
        to={`/live/${item.id}`}
        className="relative block aspect-video w-full shrink-0 cursor-pointer overflow-hidden bg-background-200"
        aria-label={`观看直播：${item.title}`}
      >
        <img
          src={item.cover}
          alt={`${item.title} 的现场实时画面预览`}
          title={`${item.title} - 爆料直播现场实时画面`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/75 via-background-950/10 to-background-950/30" />
        <div className="absolute left-3 top-3 flex items-center gap-2">
          <LiveStatusBadge status={item.status} />
        </div>
        <div className="absolute right-3 top-3">
          <SignalMeter
            value={item.signal}
            sound={item.sound}
            className="rounded-full bg-background-950/60 px-2 py-1 backdrop-blur"
          />
        </div>
        <div className="absolute inset-x-3 bottom-3 flex items-center justify-between text-[11px] text-foreground-800">
          <span className="inline-flex items-center gap-1 whitespace-nowrap font-label">
            <i className="ri-eye-line" aria-hidden="true" />
            {formatViewers(item.viewers)} 人在看
          </span>
          <span className="inline-flex items-center gap-1 whitespace-nowrap font-label">
            <i className="ri-map-pin-line" aria-hidden="true" />
            {item.location}
          </span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-center gap-2 text-[11px] text-foreground-500">
          {typeof index === "number" && (
            <span className="font-label text-primary-500">
              {index < 10 ? `0${index}` : index}
            </span>
          )}
          <span className="whitespace-nowrap text-secondary-700">{item.category}</span>
          <span className="h-3 w-px bg-background-300" aria-hidden="true" />
          <span className="whitespace-nowrap font-label">{item.startAt}</span>
        </div>

        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link
            to={`/live/${item.id}`}
            className="cursor-pointer transition-colors hover:text-primary-500"
          >
            {item.title}
          </Link>
        </h3>

        <p className="line-clamp-2 text-[13px] leading-relaxed text-foreground-600">
          {item.summary}
        </p>

        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-[11px] text-foreground-500">
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <i className="ri-user-voice-line" aria-hidden="true" />
            {item.host}
          </span>
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <i className="ri-heart-3-line" aria-hidden="true" />
            {formatViewers(item.likes)}
          </span>
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <i className="ri-timer-line" aria-hidden="true" />
            {item.duration}
          </span>
        </div>
      </div>
    </article>
  );
}